// Wishlist page (sections/main-wishlist.liquid): renders the saved products as cards with remove (✕)
// and add-to-bag buttons, a Share button (link with the products in the URL), and Recently Viewed.
// A shared link (?items=a,b) shows those products with hearts so the visitor can save them.
if (!customElements.get('wishlist-page')) {
  customElements.define(
    'wishlist-page',
    class WishlistPage extends HTMLElement {
      connectedCallback() {
        this.store = window.CRWishlist;
        if (!this.store) return;
        this.grid = this.querySelector('[data-wishlist-grid]');
        this.recentGrid = this.querySelector('[data-recent-grid]');
        this.shareButton = this.querySelector('[data-wishlist-share]');
        this.shared = (new URLSearchParams(location.search).get('items') || '')
          .split(',')
          .map((h) => h.trim())
          .filter(Boolean);

        this.shareButton?.addEventListener('click', () => this.share());
        if (!this.shared.length) this.store.subscribe(() => this.render());
        this.render();
      }

      async render() {
        const shared = this.shared.length > 0;
        const handles = shared ? this.shared : this.store.items();
        this.dataset.state = shared ? 'shared' : handles.length ? 'filled' : 'empty';

        const products = (await Promise.all(handles.map((h) => this.store.product(h)))).filter(Boolean);
        this.grid.replaceChildren(...products.map((p) => this.card(p, shared ? 'heart' : 'remove')));
        if (!shared && handles.length && !products.length) this.dataset.state = 'empty';

        // Recently viewed: products seen on this browser that aren't already shown above
        const recent = this.store.recent().filter((h) => !handles.includes(h)).slice(0, 4);
        const recentProducts = (await Promise.all(recent.map((h) => this.store.product(h)))).filter(Boolean);
        this.recentGrid.replaceChildren(...recentProducts.map((p) => this.card(p, 'heart')));
        this.toggleAttribute('data-has-recent', recentProducts.length > 0);
      }

      card(product, mode) {
        const item = document.createElement('li');
        item.className = 'wishlist-card';
        const image = product.featured_image || product.images?.[0] || '';
        const url = product.url || `/products/${product.handle}`;
        const price = this.formatMoney(product.price);
        const variants = (product.variants || []).filter((v) => v.available);
        const oneSize = (product.variants || []).length === 1;

        item.innerHTML = `
          <a class="wishlist-card__link" href="${url}">
            <span class="wishlist-card__media">
              ${image ? `<img src="${image}${image.includes('?') ? '&' : '?'}width=900" alt="" loading="lazy">` : ''}
            </span>
            <span class="wishlist-card__info">
              <span class="wishlist-card__title"></span>
              <span class="wishlist-card__price">${price}</span>
            </span>
          </a>`;
        item.querySelector('.wishlist-card__title').textContent = product.title;

        if (mode === 'remove') {
          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'wishlist-card__remove';
          remove.setAttribute('aria-label', `Remove ${product.title} from wishlist`);
          remove.innerHTML = '<svg viewBox="0 0 18 18" aria-hidden="true" focusable="false"><path d="M3 3l12 12M15 3L3 15" stroke="currentColor" stroke-width="1.2"/></svg>';
          remove.addEventListener('click', () => {
            this.store.remove(product.handle);
            this.store.toast(product.handle, false);
          });
          item.appendChild(remove);
        } else {
          const heart = document.createElement('wishlist-button');
          heart.className = 'wishlist-heart wishlist-card__heart';
          heart.dataset.handle = product.handle;
          heart.dataset.title = product.title;
          heart.innerHTML = document.getElementById('WishlistHeartTemplate')?.innerHTML || '';
          item.appendChild(heart);
        }

        if (variants.length) {
          const bag = document.createElement(oneSize ? 'button' : 'a');
          bag.className = 'wishlist-card__bag';
          bag.setAttribute('aria-label', `Add ${product.title} to bag`);
          bag.innerHTML = document.getElementById('WishlistBagIconTemplate')?.innerHTML || '';
          if (oneSize) {
            bag.type = 'button';
            bag.addEventListener('click', () => this.addToBag(variants[0].id, bag));
          } else {
            bag.href = url; // several sizes: choose on the product page
          }
          item.appendChild(bag);
        }
        return item;
      }

      async addToBag(variantId, button) {
        button.disabled = true;
        const drawer = document.querySelector('cart-drawer');
        const sections = drawer?.getSectionsToRender?.().map((s) => s.id) || [];
        try {
          const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections, sections_url: location.pathname }),
          });
          const state = await response.json();
          if (!response.ok) throw new Error(state.description || 'Could not add to bag');
          if (drawer?.renderContents) {
            drawer.classList.remove('is-empty');
            drawer.renderContents({ ...state, id: variantId });
          } else {
            location.href = window.routes?.cart_url || '/cart';
          }
        } catch (error) {
          alert(error.message);
        } finally {
          button.disabled = false;
        }
      }

      async share() {
        const url = new URL(location.href);
        url.searchParams.set('items', this.store.items().join(','));
        const data = { title: document.title, url: url.toString() };
        try {
          if (navigator.share) {
            await navigator.share(data);
            return;
          }
          await navigator.clipboard.writeText(data.url);
          this.shareButton.querySelector('span').textContent = 'Link copied';
          setTimeout(() => (this.shareButton.querySelector('span').textContent = 'Share'), 2500);
        } catch (error) {
          // Share sheet dismissed
        }
      }

      formatMoney(cents) {
        if (typeof cents !== 'number') return '';
        const amount = cents / 100;
        const whole = Number.isInteger(amount);
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'USD',
          minimumFractionDigits: whole ? 0 : 2,
          maximumFractionDigits: whole ? 0 : 2,
        }).format(amount);
      }
    }
  );
}
